/**
 * participantSubmissionService.ts
 *
 * Adapter between the new participant capture flows and Firebase.
 * Each capture type has its own typed function.
 *
 * Features:
 *   - Video compression (720p / 1.5 Mbps) before upload
 *   - Cancellable uploads via AbortSignal
 *   - Per-take progress tracking
 *   - Parallel photo uploads
 *   - Persist-before-upload: blobs saved to IndexedDB before network
 *     upload so they survive page navigation
 *   - UploadTask resume on network recovery (within same session)
 */

import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  type UploadTask,
} from 'firebase/storage';
import { doc, collection, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { storage } from './firebaseConfig';
import { db } from './firebaseConfig';
import { getCurrentUser } from './authService';
import { isPreviewSession } from './previewMode';
import { MediaType } from '../types/task.types';
import { Submission } from '../types/submission.types';
import type { SignificationSubmission, SignificationResponse } from '../types/signification.types';
import type { PhotoAnnotation } from '../types/photo.types';
import { compressVideo, formatBytes } from './videoCompressionService';
import { savePendingUpload, removePendingUpload } from './offlineSyncService';
import { collectPinAudioBlobs, substitutePinAudioUrls, type PinAudioResult } from './voicePinSubmit';

// Schema configuration — verified against feature/antigravity-integration
const SUBMISSIONS_COLLECTION = 'submissions';
const STORAGE_PATH_PREFIX = 'users'; // users/{uid}/submissions/{subId}/{file}

// ─── Types ───────────────────────────────────────────────────────

export interface SubmissionProgress {
  /** 0–100 overall progress */
  percent: number;
  /** Which phase we're in */
  status: 'uploading' | 'processing' | 'complete' | 'error';
  /** Human-readable label */
  message: string;
  /** Error detail (only when status === 'error') */
  error?: string;
}

export type ProgressCallback = (progress: SubmissionProgress) => void;

interface BaseSubmitParams {
  projectId: string;
  participantId: string;
  taskId: string;
  onProgress?: ProgressCallback;
  /** Pass an AbortSignal to make the submission cancellable. */
  signal?: AbortSignal;
  /** Submission lineage — set for redo/addendum submissions */
  parentSubmissionId?: string | null;
  submissionType?: 'original' | 'redo' | 'addendum';
  attemptNumber?: number;
}

// ─── Internal helpers ────────────────────────────────────────────

function getUserId(): string {
  const user = getCurrentUser();
  if (!user) throw new Error('User not authenticated');
  return user.uid;
}

/**
 * Read orgId directly from the user's JWT claim. Fallback path when the
 * project-lookup returns an empty organizationId — without it, the submission
 * doc lands with organizationId='' and the participant's onSnapshot listener
 * (which filters by organizationId) can't see their own submission.
 */
async function getClaimOrgId(): Promise<string> {
  const user = getCurrentUser();
  if (!user) return '';
  try {
    const tr = await user.getIdTokenResult();
    return (tr.claims.orgId as string) || '';
  } catch {
    return '';
  }
}

function throwIfAborted(signal?: AbortSignal) {
  if (signal?.aborted) throw new DOMException('Cancelled', 'AbortError');
}

/** Handle returned by startUpload — exposes the UploadTask for resume. */
interface UploadHandle {
  /** Resolves with the download URL on success. */
  promise: Promise<string>;
  /** Firebase UploadTask — call .resume() after network recovery. */
  task: UploadTask;
}

/**
 * Start a resumable upload. Returns both the promise and the raw UploadTask
 * so callers can call task.resume() on network recovery instead of restarting.
 */
function startUpload(
  blob: Blob | File,
  submissionId: string,
  fileName: string,
  onProgress?: (percent: number) => void,
  signal?: AbortSignal,
): UploadHandle {
  throwIfAborted(signal);

  // Preview uploads nothing. Simulate the progress callbacks so the capture UI
  // behaves identically, and hand back a local blob URL so playback still works
  // in-session. Media the researcher records in preview stays on their device.
  if (isPreviewSession()) {
    const localUrl = URL.createObjectURL(blob);
    onProgress?.(100);
    // No real UploadTask exists — nothing is in flight, so resume/cancel/pause
    // are no-ops for the callers that wire them to retry and abort handling.
    const noopTask = {
      resume: () => true,
      pause: () => true,
      cancel: () => true,
      on: () => () => { /* no events */ },
    } as unknown as UploadTask;
    return { promise: Promise.resolve(localUrl), task: noopTask };
  }

  const userId = getUserId();
  const storagePath = `${STORAGE_PATH_PREFIX}/${userId}/submissions/${submissionId}/${fileName}`;
  const storageRef = ref(storage, storagePath);

  const contentType = blob.type || 'application/octet-stream';
  const task = uploadBytesResumable(storageRef, blob, { contentType });

  // Wire abort → Firebase cancel
  const onAbort = () => task.cancel();
  signal?.addEventListener('abort', onAbort, { once: true });

  const promise = new Promise<string>((resolve, reject) => {
    task.on(
      'state_changed',
      (snapshot) => {
        const pct = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        onProgress?.(pct);
      },
      (error) => {
        signal?.removeEventListener('abort', onAbort);
        if ((error as any)?.code === 'storage/canceled') {
          reject(new DOMException('Upload cancelled', 'AbortError'));
        } else {
          console.error('[participantSubmission] upload error:', error);
          reject(error);
        }
      },
      async () => {
        signal?.removeEventListener('abort', onAbort);
        const downloadURL = await getDownloadURL(task.snapshot.ref);
        resolve(downloadURL);
      },
    );
  });

  return { promise, task };
}

/**
 * Upload a single blob/file to Firebase Storage with progress tracking.
 * Convenience wrapper around startUpload that just returns the URL.
 */
async function uploadBlob(
  blob: Blob | File,
  submissionId: string,
  fileName: string,
  onProgress?: (percent: number) => void,
  signal?: AbortSignal,
): Promise<string> {
  return startUpload(blob, submissionId, fileName, onProgress, signal).promise;
}

// NOTE: stale pending uploads are purged by UploadManager.recover(), AFTER it
// re-enqueues them and with the live ids exempted. This used to be an
// unconditional module-load call, which raced recover() and won — destroying
// the participant's only copy of any media older than 24h.

/**
 * Fire-and-forget callable to kick off server-side transcription for
 * standalone audio or photo voice-pins. Exported for the upload manager —
 * transcribeSubmission is an HTTP callable, NOT a Firestore trigger, so
 * every path that creates a submission doc must invoke it explicitly.
 */
export async function triggerTranscription(submissionId: string, projectId: string): Promise<void> {
  const fns = getFunctions();
  const call = httpsCallable(fns, 'transcribeSubmission');
  await call({ submissionId, projectId, languageCode: 'en-US' });
}

/**
/**
 * Defense-in-depth: deep-strip any Blob value from photoAnnotations
 * before the Firestore write. A Blob anywhere in the doc makes setDoc()
 * throw "Unsupported field value", which (via the upload manager) turns
 * into an infinite retry-loop and a permanently failed submission.
 * Callers SHOULD upload + substitute audio first (so recordings aren't
 * lost); this only guarantees a stray Blob can never crash the write.
 * Scoped to photoAnnotations (plain data) — never run on the full
 * payload, which contains serverTimestamp() sentinels.
 */
function stripBlobsDeep<T>(value: T): T {
  if (typeof Blob !== 'undefined' && value instanceof Blob) return undefined as any;
  if (Array.isArray(value)) return value.map(stripBlobsDeep) as any;
  if (value && typeof value === 'object') {
    const out: any = {};
    for (const [k, v] of Object.entries(value)) {
      const sv = stripBlobsDeep(v);
      if (sv !== undefined) out[k] = sv;
    }
    return out;
  }
  return value;
}

/**
 * Create the Firestore submission document.
 * Exported for use by the upload manager.
 */
export async function createSubmissionDoc(data: {
  /** Explicit doc id. Pass one to make the write idempotent across retries;
      omit for a fresh random id. */
  submissionId?: string;
  projectId: string;
  participantId: string;
  taskId: string;
  mediaType: MediaType;
  mediaUrls: string[];
  notes: string;
  location?: string;
  parentSubmissionId?: string | null;
  submissionType?: 'original' | 'redo' | 'addendum';
  attemptNumber?: number;
  photoAnnotations?: PhotoAnnotation[];
}): Promise<string> {
  // Preview never persists. Returning a synthetic id lets the whole UI journey
  // (progress → success → task ticked off) complete unchanged; that state lives
  // in React for the session and is gone on reload, which is the honest
  // depiction of a preview.
  if (isPreviewSession()) {
    return `preview-${Date.now()}`;
  }

  const userId = getUserId();

  const submissionData: Omit<Submission, 'id'> = {
    projectId: data.projectId,
    participantId: data.participantId,
    taskId: data.taskId,
    mediaType: data.mediaType,
    mediaUrls: data.mediaUrls,
    notes: data.notes,
    timestamp: new Date().toISOString(),
    location: data.location || '',
    tags: [],
    submittedBy: userId,
    parentSubmissionId: data.parentSubmissionId || null,
    submissionType: data.submissionType || 'original',
    attemptNumber: data.attemptNumber || 1,
  };

  const submissionRef = data.submissionId
    ? doc(db, SUBMISSIONS_COLLECTION, data.submissionId)
    : doc(collection(db, SUBMISSIONS_COLLECTION));

  // Resolve organizationId. Project-lookup is primary (authoritative per-project);
  // fall back to the user's JWT claim so the submission doc always carries a
  // real orgId — an empty organizationId makes the participant's own onSnapshot
  // listener skip the submission (it filters by where organizationId == claim).
  let orgId = '';
  if (data.projectId) {
    try {
      const projectDoc = await Promise.race([
        getDoc(doc(db, 'projects', data.projectId)),
        new Promise<never>((_, reject) => setTimeout(() => reject(new Error('Project lookup timeout')), 5000)),
      ]);
      if (projectDoc.exists()) {
        orgId = projectDoc.data()?.organizationId || '';
      }
    } catch (error) {
      console.error('[participantSubmission] Error fetching project for organizationId:', error);
    }
  }
  if (!orgId) {
    orgId = await getClaimOrgId();
    console.warn('[participantSubmission] project-lookup orgId empty; using claim orgId:', orgId);
  }

  // Look up taskCreatorId from task — use a timeout to prevent hanging
  let taskCreatorId: string | undefined;
  if (data.taskId) {
    try {
      console.log('[participantSubmission] Looking up task:', data.taskId);
      const taskDoc = await Promise.race([
        getDoc(doc(db, 'tasks', data.taskId)),
        new Promise<never>((_, reject) => setTimeout(() => reject(new Error('Task lookup timeout')), 5000)),
      ]);
      if (taskDoc.exists()) {
        taskCreatorId = taskDoc.data()?.createdBy;
      }
      console.log('[participantSubmission] Task creatorId:', taskCreatorId);
    } catch (error) {
      console.error('[participantSubmission] Error fetching task for creatorId:', error);
    }
  }

  let safePhotoAnnotations = data.photoAnnotations;
  if (data.photoAnnotations) {
    safePhotoAnnotations = stripBlobsDeep(data.photoAnnotations);
    if (JSON.stringify(safePhotoAnnotations) !== JSON.stringify(data.photoAnnotations)) {
      console.warn('[participantSubmission] Stripped a Blob from photoAnnotations before write — caller should have uploaded it first (submission audio may be incomplete).');
    }
  }

  const docPayload = {
    ...submissionData,
    organizationId: orgId,
    ...(taskCreatorId ? { taskCreatorId } : {}),
    ...(safePhotoAnnotations ? { photoAnnotations: safePhotoAnnotations } : {}),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  console.log('[participantSubmission] Writing submission doc:', submissionRef.id, 'orgId:', orgId, 'projectId:', data.projectId, 'userId:', userId);

  try {
    await Promise.race([
      setDoc(submissionRef, docPayload),
      new Promise<never>((_, reject) => setTimeout(() => reject(new Error('Firestore setDoc timeout after 10s')), 10000)),
    ]);
    console.log('[participantSubmission] Submission doc written successfully');
  } catch (writeErr) {
    console.error('[participantSubmission] setDoc failed:', writeErr);
    throw writeErr;
  }

  return submissionRef.id;
}

export function makeFileName(mediaType: string, index: number, ext: string): string {
  return `${mediaType}_${Date.now()}_${index}.${ext}`;
}

export function getExtFromMime(mime: string, fallback: string): string {
  const map: Record<string, string> = {
    'video/webm': 'webm', 'video/mp4': 'mp4',
    'audio/webm': 'webm', 'audio/ogg': 'ogg', 'audio/mp4': 'm4a', 'audio/mpeg': 'mp3',
    'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp',
    'application/pdf': 'pdf',
  };
  return map[mime] || fallback;
}

// ─── Public API — one function per capture type ──────────────────

/**
 * Submit an audio recording.
 */
export async function submitAudioResponse(
  params: BaseSubmitParams & { audioBlob: Blob; notes: string },
): Promise<string> {
  const { projectId, participantId, taskId, audioBlob, notes, onProgress, signal, parentSubmissionId, submissionType, attemptNumber } = params;
  const submissionId = doc(collection(db, SUBMISSIONS_COLLECTION)).id;

  // Persist blob to IndexedDB so it survives page navigation
  let pendingId: number | undefined;
  try {
    pendingId = await savePendingUpload(
      { submissionId, projectId, participantId, taskId, mediaType: 'audio', notes },
      [audioBlob],
    );
  } catch { /* IndexedDB unavailable — continue without persistence */ }

  try {
    throwIfAborted(signal);
    onProgress?.({ percent: 0, status: 'uploading', message: 'Uploading audio...' });
    const ext = getExtFromMime(audioBlob.type, 'webm');
    const url = await uploadBlob(audioBlob, submissionId, makeFileName('audio', 0, ext), (pct) => {
      onProgress?.({ percent: Math.round(pct * 0.9), status: 'uploading', message: 'Uploading audio...' });
    }, signal);

    throwIfAborted(signal);
    onProgress?.({ percent: 90, status: 'processing', message: 'Saving submission...' });
    await createSubmissionDoc({ projectId, participantId, taskId, mediaType: MediaType.Audio, mediaUrls: [url], notes, parentSubmissionId, submissionType, attemptNumber });

    // Fire-and-forget transcription, matching the photo voice-pin path. Without
    // this a standalone audio submission was never transcribed at all — the
    // only routes were voice pins on a photo, or a researcher clicking
    // Transcribe in the deep dive.
    if (!isPreviewSession()) {
      triggerTranscription(submissionId, projectId).catch(err => {
        console.warn('transcribeSubmission trigger failed (non-fatal):', err);
      });
    }

    onProgress?.({ percent: 100, status: 'complete', message: 'Submitted!' });
    if (pendingId !== undefined) removePendingUpload(pendingId).catch(() => {});
    return submissionId;
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      if (pendingId !== undefined) removePendingUpload(pendingId).catch(() => {});
      throw err;
    }
    const msg = err instanceof Error ? err.message : String(err);
    onProgress?.({ percent: 0, status: 'error', message: 'Upload failed', error: msg });
    throw err;
  }
}

/**
 * Submit photo(s).
 */
export async function submitPhotoResponse(
  params: BaseSubmitParams & { photoBlobs: Blob[]; notes: string; photoAnnotations?: PhotoAnnotation[] },
): Promise<string> {
  const { projectId, participantId, taskId, photoBlobs, notes, photoAnnotations, onProgress, signal, parentSubmissionId, submissionType, attemptNumber } = params;
  const submissionId = doc(collection(db, SUBMISSIONS_COLLECTION)).id;

  let pendingId: number | undefined;
  try {
    pendingId = await savePendingUpload(
      { submissionId, projectId, participantId, taskId, mediaType: 'photo', notes },
      photoBlobs,
    );
  } catch { /* IndexedDB unavailable */ }

  try {
    // Upload all photos in parallel for faster submission
    const label = photoBlobs.length > 1
      ? `Uploading ${photoBlobs.length} photos...`
      : 'Uploading photo...';
    const perPhotoProgress = new Array(photoBlobs.length).fill(0);

    const uploadPromises = photoBlobs.map((blob, i) => {
      const ext = getExtFromMime(blob.type, 'jpg');
      return uploadBlob(blob, submissionId, makeFileName('photo', i, ext), (pct) => {
        perPhotoProgress[i] = pct;
        const avg = perPhotoProgress.reduce((a, b) => a + b, 0) / photoBlobs.length;
        onProgress?.({ percent: Math.round(avg * 0.9), status: 'uploading', message: label });
      }, signal);
    });

    const urls = await Promise.all(uploadPromises);

    // Upload pin audios (if any).
    let finalAnnotations = photoAnnotations;
    if (photoAnnotations && photoAnnotations.length > 0) {
      const pinJobs = collectPinAudioBlobs(photoAnnotations);
      if (pinJobs.length > 0) {
        onProgress?.({ percent: 90, status: 'uploading', message: `Uploading ${pinJobs.length} voice note${pinJobs.length > 1 ? 's' : ''}...` });
        const pinResults: PinAudioResult[] = await Promise.all(
          pinJobs.map(async (job) => {
            const ext = getExtFromMime(job.blob.type, 'webm');
            const fileName = `pin-audio_${job.photoIndex}_${job.pinId}_${Date.now()}.${ext}`;
            const url = await uploadBlob(job.blob, submissionId, fileName, undefined, signal);
            return { photoIndex: job.photoIndex, pinId: job.pinId, url };
          }),
        );
        finalAnnotations = substitutePinAudioUrls(photoAnnotations, pinResults);
      } else {
        // Strip any stray audioBlob fields (defensive).
        finalAnnotations = substitutePinAudioUrls(photoAnnotations, []);
      }
    }

    throwIfAborted(signal);
    onProgress?.({ percent: 95, status: 'processing', message: 'Saving submission...' });
    await createSubmissionDoc({ projectId, participantId, taskId, mediaType: MediaType.Photo, mediaUrls: urls, notes, photoAnnotations: finalAnnotations, parentSubmissionId, submissionType, attemptNumber });

    // Fire-and-forget: kick off transcription if any voice pins were uploaded.
    if (finalAnnotations && finalAnnotations.some(a => a.pins.some(p => !!p.audioUrl))) {
      triggerTranscription(submissionId, projectId).catch(err => {
        console.warn('transcribeSubmission trigger failed (non-fatal):', err);
      });
    }

    onProgress?.({ percent: 100, status: 'complete', message: 'Submitted!' });
    if (pendingId !== undefined) removePendingUpload(pendingId).catch(() => {});
    return submissionId;
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      if (pendingId !== undefined) removePendingUpload(pendingId).catch(() => {});
      throw err;
    }
    const msg = err instanceof Error ? err.message : String(err);
    onProgress?.({ percent: 0, status: 'error', message: 'Upload failed', error: msg });
    throw err;
  }
}

/**
 * Submit video recording(s).
 *
 * Each take is compressed to 720p / 1.5 Mbps before upload.
 * Progress phases per take: compress (40%) → upload (60%).
 */
export async function submitVideoResponse(
  params: BaseSubmitParams & { videoBlobs: Blob[]; notes: string },
): Promise<string> {
  const { projectId, participantId, taskId, videoBlobs, notes, onProgress, signal, parentSubmissionId, submissionType, attemptNumber } = params;
  const submissionId = doc(collection(db, SUBMISSIONS_COLLECTION)).id;

  let pendingId: number | undefined;
  try {
    pendingId = await savePendingUpload(
      { submissionId, projectId, participantId, taskId, mediaType: 'video', notes },
      videoBlobs,
    );
  } catch { /* IndexedDB unavailable */ }

  try {
    const urls: string[] = [];
    const total = videoBlobs.length;
    const perTake = 90 / total; // each take's share of the 0-90% range

    for (let i = 0; i < total; i++) {
      throwIfAborted(signal);
      const base = i * perTake;

      // ── Phase 1: Compress ──
      const compressLabel = total > 1
        ? `Compressing take ${i + 1} of ${total}...`
        : 'Compressing video...';

      onProgress?.({ percent: Math.round(base), status: 'uploading', message: compressLabel });

      const result = await compressVideo(
        videoBlobs[i],
        (pct) => {
          const progress = base + (pct / 100) * perTake * 0.4;
          onProgress?.({ percent: Math.round(progress), status: 'uploading', message: compressLabel });
        },
        signal,
      );

      throwIfAborted(signal);

      // ── Phase 2: Upload ──
      const sizeInfo = result.skipped
        ? ''
        : ` (${result.savings}% smaller — ${formatBytes(result.compressedSize)})`;
      const uploadLabel = total > 1
        ? `Uploading take ${i + 1} of ${total}${sizeInfo}...`
        : `Uploading video${sizeInfo}...`;

      const compressDone = base + perTake * 0.4;
      onProgress?.({ percent: Math.round(compressDone), status: 'uploading', message: uploadLabel });

      const ext = getExtFromMime(result.blob.type, 'webm');
      const url = await uploadBlob(
        result.blob,
        submissionId,
        makeFileName('video', i, ext),
        (pct) => {
          const progress = compressDone + (pct / 100) * perTake * 0.6;
          onProgress?.({ percent: Math.round(progress), status: 'uploading', message: uploadLabel });
        },
        signal,
      );
      urls.push(url);
    }

    throwIfAborted(signal);
    onProgress?.({ percent: 90, status: 'processing', message: 'Saving submission...' });
    await createSubmissionDoc({ projectId, participantId, taskId, mediaType: MediaType.Video, mediaUrls: urls, notes, parentSubmissionId, submissionType, attemptNumber });

    onProgress?.({ percent: 100, status: 'complete', message: 'Submitted!' });
    if (pendingId !== undefined) removePendingUpload(pendingId).catch(() => {});
    return submissionId;
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      if (pendingId !== undefined) removePendingUpload(pendingId).catch(() => {});
      throw err;
    }
    const msg = err instanceof Error ? err.message : String(err);
    onProgress?.({ percent: 0, status: 'error', message: 'Upload failed', error: msg });
    throw err;
  }
}

/**
 * Submit a text response with optional file attachment.
 */
export async function submitTextResponse(
  params: BaseSubmitParams & { text: string; file: File | null },
): Promise<string> {
  const { projectId, participantId, taskId, text, file, onProgress, signal, parentSubmissionId, submissionType, attemptNumber } = params;
  const submissionId = doc(collection(db, SUBMISSIONS_COLLECTION)).id;

  // Persist file blob to IndexedDB so it survives page navigation
  let pendingId: number | undefined;
  if (file) {
    try {
      pendingId = await savePendingUpload(
        { submissionId, projectId, participantId, taskId, mediaType: 'text', notes: text },
        [file],
      );
    } catch { /* IndexedDB unavailable */ }
  }

  try {
    const urls: string[] = [];

    if (file) {
      throwIfAborted(signal);
      onProgress?.({ percent: 0, status: 'uploading', message: 'Uploading attachment...' });
      const ext = getExtFromMime(file.type, 'bin');
      let lastReportedPct = 0;
      const url = await uploadBlob(file, submissionId, makeFileName('attachment', 0, ext), (pct) => {
        const scaled = Math.round(pct * 0.9);
        if (scaled > lastReportedPct + 1 || pct >= 100) {
          lastReportedPct = scaled;
          onProgress?.({ percent: scaled, status: 'uploading', message: 'Uploading attachment...' });
        }
      }, signal);
      urls.push(url);
    } else {
      onProgress?.({ percent: 30, status: 'uploading', message: 'Submitting response...' });
    }

    throwIfAborted(signal);
    onProgress?.({ percent: 90, status: 'processing', message: 'Saving submission...' });
    await createSubmissionDoc({ projectId, participantId, taskId, mediaType: MediaType.Text, mediaUrls: urls, notes: text, parentSubmissionId, submissionType, attemptNumber });

    onProgress?.({ percent: 100, status: 'complete', message: 'Submitted!' });
    if (pendingId !== undefined) removePendingUpload(pendingId).catch(() => {});
    return submissionId;
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      if (pendingId !== undefined) removePendingUpload(pendingId).catch(() => {});
      throw err;
    }
    const msg = err instanceof Error ? err.message : String(err);
    onProgress?.({ percent: 0, status: 'error', message: 'Upload failed', error: msg });
    throw err;
  }
}

/**
 * Submit a screen recording.
 */
export async function submitScreenResponse(
  params: BaseSubmitParams & { videoBlob: Blob; notes: string },
): Promise<string> {
  const { projectId, participantId, taskId, videoBlob, notes, onProgress, signal, parentSubmissionId, submissionType, attemptNumber } = params;
  const submissionId = doc(collection(db, SUBMISSIONS_COLLECTION)).id;

  // Persist blob to IndexedDB so it survives page navigation
  let pendingId: number | undefined;
  try {
    pendingId = await savePendingUpload(
      { submissionId, projectId, participantId, taskId, mediaType: 'screen', notes },
      [videoBlob],
    );
  } catch { /* IndexedDB unavailable */ }

  try {
    throwIfAborted(signal);
    onProgress?.({ percent: 0, status: 'uploading', message: 'Uploading screen recording...' });
    const ext = getExtFromMime(videoBlob.type, 'webm');
    const url = await uploadBlob(videoBlob, submissionId, makeFileName('screen', 0, ext), (pct) => {
      onProgress?.({ percent: Math.round(pct * 0.9), status: 'uploading', message: 'Uploading screen recording...' });
    }, signal);

    throwIfAborted(signal);
    onProgress?.({ percent: 90, status: 'processing', message: 'Saving submission...' });
    await createSubmissionDoc({ projectId, participantId, taskId, mediaType: MediaType.Screen, mediaUrls: [url], notes, parentSubmissionId, submissionType, attemptNumber });

    onProgress?.({ percent: 100, status: 'complete', message: 'Submitted!' });
    if (pendingId !== undefined) removePendingUpload(pendingId).catch(() => {});
    return submissionId;
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      if (pendingId !== undefined) removePendingUpload(pendingId).catch(() => {});
      throw err;
    }
    const msg = err instanceof Error ? err.message : String(err);
    onProgress?.({ percent: 0, status: 'error', message: 'Upload failed', error: msg });
    throw err;
  }
}

/**
 * Submit a signification response.
 *
 * Audio narratives (if any) are uploaded to Storage first.
 * Uses IndexedDB safety net (savePendingUpload) for offline resilience.
 */
export async function submitSignificationResponse(
  params: BaseSubmitParams & {
    significationData: SignificationSubmission;
    audioBlobs?: Blob[];
  },
): Promise<string> {
  const { projectId, participantId, taskId, significationData, audioBlobs = [], onProgress, signal, parentSubmissionId, submissionType, attemptNumber } = params;
  const submissionId = doc(collection(db, SUBMISSIONS_COLLECTION)).id;

  // Persist audio blobs to IndexedDB so they survive page navigation
  let pendingId: number | undefined;
  try {
    // significationData is the actual answer set — persisting only the audio
    // blobs left a failed submit with media and no responses.
    pendingId = await savePendingUpload(
      { submissionId, projectId, participantId, taskId, mediaType: 'signification', notes: '', significationData },
      audioBlobs,
    );
  } catch { /* IndexedDB unavailable — continue without persistence */ }

  try {
    throwIfAborted(signal);

    // Upload audio narrative blobs (if any) and patch URLs into responses
    if (audioBlobs.length > 0) {
      onProgress?.({ percent: 0, status: 'uploading', message: 'Uploading audio narratives...' });
      let audioIdx = 0;
      for (const response of significationData.responses) {
        if (audioIdx < audioBlobs.length && !response.narrativeAudioUrl && response.narrative !== undefined) {
          const blob = audioBlobs[audioIdx];
          const ext = getExtFromMime(blob.type, 'webm');
          const url = await uploadBlob(blob, submissionId, makeFileName('narrative', audioIdx, ext), (pct) => {
            const progress = (audioIdx / audioBlobs.length + pct / 100 / audioBlobs.length) * 70;
            onProgress?.({ percent: Math.round(progress), status: 'uploading', message: 'Uploading audio narratives...' });
          }, signal);
          response.narrativeAudioUrl = url;
          audioIdx++;
        }
      }
    }

    throwIfAborted(signal);
    onProgress?.({ percent: 80, status: 'processing', message: 'Saving submission...' });

    // Build submission document with signification data
    const userId = getUserId();
    const submissionRef = doc(db, SUBMISSIONS_COLLECTION, submissionId);

    let orgId = '';
    if (projectId) {
      try {
        const projectDoc = await Promise.race([
          getDoc(doc(db, 'projects', projectId)),
          new Promise<never>((_, reject) => setTimeout(() => reject(new Error('Project lookup timeout')), 5000)),
        ]);
        if (projectDoc.exists()) orgId = projectDoc.data()?.organizationId || '';
      } catch { /* timeout — proceed without orgId */ }
    }
    if (!orgId) orgId = await getClaimOrgId();

    await setDoc(submissionRef, {
      projectId,
      participantId,
      taskId,
      mediaType: 'signification',
      mediaUrls: [],
      notes: '',
      timestamp: new Date().toISOString(),
      location: '',
      tags: [],
      submittedBy: userId,
      parentSubmissionId: parentSubmissionId || null,
      submissionType: submissionType || 'original',
      attemptNumber: attemptNumber || 1,
      organizationId: orgId,
      significationData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    onProgress?.({ percent: 100, status: 'complete', message: 'Submitted!' });
    if (pendingId !== undefined) removePendingUpload(pendingId).catch(() => {});
    return submissionId;
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      if (pendingId !== undefined) removePendingUpload(pendingId).catch(() => {});
      throw err;
    }
    const msg = err instanceof Error ? err.message : String(err);
    onProgress?.({ percent: 0, status: 'error', message: 'Submission failed', error: msg });
    throw err;
  }
}

/**
 * Submit a guided capture.
 */
export async function submitGuidedResponse(
  params: BaseSubmitParams & { file: File; notes: string },
): Promise<string> {
  const { projectId, participantId, taskId, file, notes, onProgress, signal, parentSubmissionId, submissionType, attemptNumber } = params;
  const submissionId = doc(collection(db, SUBMISSIONS_COLLECTION)).id;

  // Persist blob to IndexedDB so it survives page navigation
  let pendingId: number | undefined;
  try {
    pendingId = await savePendingUpload(
      { submissionId, projectId, participantId, taskId, mediaType: 'guided', notes },
      [file],
    );
  } catch { /* IndexedDB unavailable */ }

  try {
    throwIfAborted(signal);
    onProgress?.({ percent: 0, status: 'uploading', message: 'Uploading file...' });
    const ext = getExtFromMime(file.type, 'bin');
    const url = await uploadBlob(file, submissionId, makeFileName('guided', 0, ext), (pct) => {
      onProgress?.({ percent: Math.round(pct * 0.9), status: 'uploading', message: 'Uploading file...' });
    }, signal);

    throwIfAborted(signal);
    onProgress?.({ percent: 90, status: 'processing', message: 'Saving submission...' });
    await createSubmissionDoc({ projectId, participantId, taskId, mediaType: MediaType.Video, mediaUrls: [url], notes, parentSubmissionId, submissionType, attemptNumber });

    onProgress?.({ percent: 100, status: 'complete', message: 'Submitted!' });
    if (pendingId !== undefined) removePendingUpload(pendingId).catch(() => {});
    return submissionId;
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      if (pendingId !== undefined) removePendingUpload(pendingId).catch(() => {});
      throw err;
    }
    const msg = err instanceof Error ? err.message : String(err);
    onProgress?.({ percent: 0, status: 'error', message: 'Upload failed', error: msg });
    throw err;
  }
}
